// src/components/league-creation/league-type/LotterySettings.js
import React from 'react';
import { Dices, Award, TrendingDown } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { parseIntOrPrev } from '../../../services/formUtils';

const LotterySettings = ({ formData, onInputChange, error }) => {
  // Calculate max lottery picks: can't exceed (total teams - excluded teams)
  const maxLotteryPicks = formData.max_teams - (formData.lottery_top_excluded ?? 0);
  
  return (
    <div 
      className={`border-2 ${dynastyTheme.classes.border.primary}/50 ${dynastyTheme.classes.bg.primary}/5`}
      style={{ 
        borderRadius: dynastyTheme.tokens.radius.lg,
        padding: dynastyTheme.tokens.spacing.lg
      }}
    >
      <div className="flex items-center mb-4" style={{ gap: dynastyTheme.tokens.spacing.sm }}>
        <Dices className={dynastyTheme.classes.text.primary} style={{ width: '1.25rem', height: '1.25rem' }} />
        <h4 className={`${dynastyTheme.classes.text.white} font-bold text-lg`}>
          Lottery Settings
        </h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Number of Lottery Picks */}
        <div>
          <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white} flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.xs }}>
            <Award style={{ width: '0.875rem', height: '0.875rem' }} />
            Lottery Picks
          </label>
          <input
            type="number"
            value={formData.lottery_num_picks}
            onChange={(e) => onInputChange('lottery_num_picks', parseIntOrPrev(e.target.value, formData.lottery_num_picks))}
            className={`${dynastyTheme.components.input} w-full`}
            min="1"
            max={maxLotteryPicks}
          />
          <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
            How many top picks are determined by lottery (1-{maxLotteryPicks})
          </p>
          {error && error.includes('Lottery picks') && (
            <p className={`${dynastyTheme.classes.text.error} text-xs mt-1`}>
              {error}
            </p>
          )}
        </div>

        {/* Top Teams Excluded */}
        <div>
          <label className={`${dynastyTheme.components.label} ${dynastyTheme.classes.text.white} flex items-center`} style={{ gap: dynastyTheme.tokens.spacing.xs }}>
            <TrendingDown style={{ width: '0.875rem', height: '0.875rem' }} />
            Top Teams Excluded
          </label>
          <input
            type="number"
            value={formData.lottery_top_excluded}
            onChange={(e) => onInputChange('lottery_top_excluded', parseIntOrPrev(e.target.value, formData.lottery_top_excluded))}
            className={`${dynastyTheme.components.input} w-full`}
            min="0"
            max="10"
          />
          <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
            How many top finishers are NOT eligible (0-10)
          </p>
          {error && error.includes('Top teams excluded') && (
            <p className={`${dynastyTheme.classes.text.error} text-xs mt-1`}>
              {error}
            </p>
          )}
        </div>
      </div>

      {/* Lottery Info Box */}
      <div 
        className={`${dynastyTheme.classes.bg.primary}/10 border ${dynastyTheme.classes.border.primary}/30`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.md,
          padding: dynastyTheme.tokens.spacing.sm
        }}
      >
        <div className="flex items-start" style={{ gap: dynastyTheme.tokens.spacing.sm }}>
          <Dices className={dynastyTheme.classes.text.primary} style={{ width: '1rem', height: '1rem', marginTop: '0.125rem' }} />
          <div>
            <h5 className={`${dynastyTheme.classes.text.white} font-semibold mb-1 text-xs`}>
              How the Lottery Works
            </h5>
            <div className={`${dynastyTheme.classes.text.neutralLight} text-xs space-y-1`}>
              <p>
                • <strong className={dynastyTheme.classes.text.primary}>NBA-style odds:</strong> Teams with worse records have better odds at top picks
              </p>
              <p>
                • <strong className={dynastyTheme.classes.text.primary}>Example:</strong> With 3 lottery picks, the worst 3 teams compete for picks 1-3
              </p>
              <p>
                • <strong className={dynastyTheme.classes.text.primary}>Excluded teams:</strong> Top finishers pick after lottery teams in standings order
              </p>
              <p>
                • <strong className={dynastyTheme.classes.text.primary}>Weighting:</strong> Each team's odds are based on their final standings position
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Example */}
      <div 
        className={`mt-3 ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.light}`}
        style={{ 
          borderRadius: dynastyTheme.tokens.radius.sm,
          padding: dynastyTheme.tokens.spacing.sm
        }}
      >
        <p className={`${dynastyTheme.classes.text.neutralLight} text-xs text-center`}>
          <span className={dynastyTheme.classes.text.primary}>Current Settings:</span>{' '}
          Top {formData.lottery_num_picks} picks via lottery • 
          {' '}Top {formData.lottery_top_excluded} teams excluded from lottery
        </p>
      </div>
    </div>
  );
};

export default LotterySettings;